import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NEVER,
  Subject,
  catchError,
  combineLatest,
  first,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { SOCKET_FOLDER_EVENT } from '../../enums/events-buffer/socket/socket-folders-event.enum';
import { SOCKET_ROOM } from '../../enums/events-buffer/socket/socket-rooms-enum';
import { Logger } from '../../interfaces/Logger';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { SocketRoomHandlerService } from '../../services/socket/socket-room-handler.service';
import { SocketService } from '../../services/socket/socket.service';
import { SignatureService } from '../service/signature.service';
import { IFolder } from './interface/product-folder.interface';
import { ProductFolderService } from './product-folder.service';

/**
 * Service responsible for handling socket events related to product folders.
 * Listens to folder creation and activation events, updates local state, and notifies subscribers.
 * Implements best practices for Angular, RxJS, and SOLID principles.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductFolderSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly productFolderService = inject(ProductFolderService);
  private readonly signatureService = inject(SignatureService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed$: Subject<void> = new Subject<void>();

  private onFolderActivated: Subject<IFolder> = new Subject<IFolder>();
  onFolderActivated$ = this.onFolderActivated.asObservable();

  private onFolderCreated: Subject<void> = new Subject<void>();
  onFolderCreated$ = this.onFolderCreated.asObservable();

  constructor() {
    /**
     * Initializes the service by checking for the folders socket room and setting up listeners.
     */
    this.checkForFoldersRoom();
  }

  /**
   * Clears all active subscriptions and signals destruction for cleanup.
   */
  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Notifies subscribers that a folder has been created.
   * Used internally after handling the socket event.
   */
  private notifyFolderCreated(): void {
    this.onFolderCreated.next();
  }

  /**
   * Notifies subscribers that a folder has been activated.
   * @param folder The folder that was activated
   */
  private notifyFolderActivated(folder: IFolder): void {
    this.onFolderActivated.next(folder);
  }

  /**
   * Subscribes to the folders socket room and initializes folder event listeners when joined.
   */
  private checkForFoldersRoom(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.folders)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.folders} 📂] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeFolderListeners();
      });
  }

  /**
   * Initializes listeners for folder creation and activation events.
   * Should be called after joining the folders socket room.
   */
  private initializeFolderListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.folders} 📂] [Folders listeners initialized]`
    );
    this.onFolderCreatedListener();
    this.onFolderActivatedListener();
  }

  /**
   * Listens for folder creation events from the socket, updates local state, and notifies subscribers.
   * Handles signature validation to prevent duplicate processing.
   */
  private onFolderCreatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.folders} 📂] [Folder created listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_FOLDER_EVENT.created)
      .pipe(
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const folder = eventResponse.payload.productFolder;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            const insertFolderIntoFolders$ =
              this.productFolderService.insertFolder(folder);

            return combineLatest([insertFolderIntoFolders$, signature$]);
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.folders} 📂] [Folder has been created]`
        );
        this.notifyFolderCreated();
      });
  }

  /**
   * Listens for folder activation/deactivation events from the socket, updates local state, and notifies subscribers.
   * Handles signature validation and updates the current folder if necessary.
   */
  private onFolderActivatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.folders} 📂] [Folder activated/deactivated listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_FOLDER_EVENT.activated)
      .pipe(
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const folder = eventResponse.payload.productFolder;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            const updateFolderIntoFolders$ =
              this.productFolderService.insertFolder(folder);

            return combineLatest([
              of(folder),
              updateFolderIntoFolders$,
              signature$,
            ]);
          }
        ),
        switchMap(([folder]: [IFolder | null, IFolder | null, string]) => {
          this.logger.debug(
            `[Socket 🔌] [Room: ${SOCKET_ROOM.folders} 📂] [Folder has been activated/deactivated]`
          );

          if (!folder) {
            return of(null);
          }

          if (
            this.productFolderService.getCurrentFolderFromCache()?._id ===
            folder?._id
          ) {
            return this.productFolderService.insertCurrentFolder(folder).pipe(
              first(),
              map(() => folder)
            );
          }

          return of(folder);
        }),
        tap((folder) => {
          if (folder) {
            this.notifyFolderActivated(folder);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
